/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { takeUntil } from 'rxjs/operators';
import { MILESTONE_LINE_LABEL_TEXT } from '@touchplan/strings';
import { dateCache, Milestone } from '@touchplan/common';
import { Point, Rectangle, utils } from 'pixi.js-legacy';
import { IDestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { ComponentManager } from '@appCanvasCommon/component-system/ComponentManager';
import { MilestoneTap } from '@appPlanApplication/timeline/milestone-line/MilestoneTap';
import { FancySprite } from '@appCanvasCommon/graphics/FancySprite';
import { TICKET_TEXTURE_OPTIONS } from '@appPlanApplication/tickets/TICKET_TEXTURE_OPTIONS';
import { cssVar } from '@touchplan/css-var';
import { AbstractDisplayLine, displayLineSettings } from '@appPlanApplication/timeline/AbstractDisplayLine';
import { IPlanMilestone } from '../../../../modules/plan/services/milestone-line.service';

/**
 * Visual configuration settings for the milestone line.
 * These settings control the appearance of both the line and its label.
 * The hitArea settings control how much of an area around the visible line can be interacted with.
 * This allows for less precise gestures and interaction with smaller objects (like using a finger to interact with a thin line)
 */
const settings = {
	labelFormat: `MMM DD YYYY`,
	hitAreaOffset: 10, // The offset from origin to start interaction detection (10 = will start 10px to the left of the visible line)
	hitAreaWidth: 40, // The width of the interaction area. Offset of 10 and 40 width means there is a 10px "grace" area on each side
	iconWidth: 20,
	iconHeight: 30,
	timelineHeight: 38, // 38px from the Plan Printing spec
};
/**
 * TodayLineDrawer is responsible for rendering the today line indicator in the canvas.
 * The today line shows the current date with a vertical line and text label.
 */
export class MilestoneLineDrawer extends AbstractDisplayLine {
	/**
	 * Container that holds milestone line elements that render on the UI timeline layer
	 * The _icon and _timelineContainer are intentionally added to a new layer because the Milestone Line
	 * draws inside of WORLD SPACE, whereas the timeline itself draws as part of SCREEN SPACE and ABOVE the World
	 * So in order to properly layer the milestone line nub so it appears ABOVE the Timeline itself,
	 * we need to draw the milestone line nub icon on a layer that draws above the timeline.
	 * @private
	 */
	private _timelineContainer: FancyContainer;
	/**
	 * We store the last Milestone as a cache to handle cases where the canvas is forced to redraw
	 * @private
	 */
	private _cachedMilestoneLine: IPlanMilestone | null = null;
	/**
	 * Component manager instance which holds logic for interaction gestures
	 * @private
	 */
	private _components: ComponentManager;
	/**
	 * Stores a reference to the timeline Milestone Icon
	 * @private
	 */
	private _icon: FancySprite;
	/**
	 * Creates a new MilestoneLineDrawer instance.
	 * @param _canvasAngularLand - Interface to Angular services/dependencies
	 * @param _application - The canvas application instance
	 */
	constructor(private _canvasAngularLand: CanvasAngularLand, _application: AbstractApplication) {
		super(_application, LayerPaths.BACKGROUND_TIMELINE_MILESTONE_LINE);
		this.setup();
		this.setupSubscribers();
	}
	/**
	 * Updates the milestone line's position based on camera transforms.
	 * This ensures the line stays correctly positioned during pan/zoom operations.
	 * @param _ - Delta Time elapsed since last update
	 */
	public update(_?: number): void {
		if (!this._container || !this._cachedMilestoneLine) {
			return;
		}
		const data: IPlanMilestone = this._cachedMilestoneLine;
		const position: Point = this.getPosition(data);
		super.update(position.x, position.y);
		this.positionIcon();
		this.updateInteractionArea();
	}

	private updateInteractionArea(): void {
		// Using 0 because we want hit area to start at origin on the y-axis
		this._container.hitArea = new Rectangle(
			-settings.hitAreaOffset,
			0,
			settings.hitAreaWidth,
			this.parentApplication.camera.getScreen().height
		);
	}
	/**
	 * Milestone line has special position logic:
	 * - If there are tickets and the milestone IS set, we need to position the line in the world (drawn on the screen)
	 * - If there are tickets and the milestone is NOT set, we need to stick to the right-edge of the screen
	 * If there are NO tickets and the milestone IS set, we need to stick to the right-edge of the screen.
	 * @param milestone
	 * @private
	 */
	private getPosition(milestone: IPlanMilestone): Point {
		if (milestone.isSet && milestone.targetX !== undefined) {
			// Y-value of 0 to start at the top of the screen
			return new Point(this.parentApplication.camera.realToScreenX(milestone.targetX), 0);
		}

		// No milestone. Position to right-edge
		const screen: Rectangle = this.parentApplication.camera.view;
		return new Point(
			this.parentApplication.camera.realToScreenX(
				this.parentApplication.camera.screenToRealX(screen.width - displayLineSettings.lineWidth)
			),
			0 // Y-value of 0 to start at the top of the screen
		);
	}
	/**
	 * Draws the milestone line visualization.
	 * Creates a vertical line and positions the rotated date label.
	 * @param milestoneLine - Milestone line data to render, defaults to cached data
	 */
	public override draw(milestoneLine: IPlanMilestone | null = this._cachedMilestoneLine): void {
		if (!milestoneLine) {
			this.hide();
			this._timelineContainer.visible = false;
			return;
		}

		this.setMilestoneLabelText(milestoneLine);
		super.draw();
		// Add the header height to the label Y to offset it from the top of the screen
		this._label.y = this._label.y + cssVar.headerHeight_px;
		this._icon.visible = true;
		this._timelineContainer.visible = true;
		this._cachedMilestoneLine = milestoneLine;
		// We are updating the position of the visual in update(). Since draw() can cause the position of the line
		// to change, we need to explicitly invoke update to ensure the update is positioned correctly.
		// If we don't do this, then the line will be out of position until the next tick (any type of interaction with the canvas)
		this.update();
	}

	/**
	 * This positions the Milestone icon on the timeline to the
	 * @private
	 */
	private positionIcon(): void {
		// calculated by taking the timeline height (38), / 2 (19), - 15 (half icon height).
		// The remainder is the amount of pixels we need to offset the icon so the center point aligns with the middle of the timeline
		const yOffset: number = settings.timelineHeight / 2 - settings.iconHeight / 2;
		this._timelineContainer.position.set(
			this._container.x,
			// Since we are drawing from top of the screen now, we need to include the header height when positioning icon
			cssVar.headerHeight_px + this._container.y + yOffset + settings.iconHeight / 2
		);
	}
	/**
	 * Updates the milestone line label based on the current milestone state
	 * @param milestone The current milestone data
	 * @private
	 */
	private setMilestoneLabelText(milestone: IPlanMilestone): void {
		// Milestone is set, potentially set the milestone name (activityName)
		if (milestone.isSet) {
			const milestoneNameLabel: string = milestone.activityName
				? milestone.activityName
				: MILESTONE_LINE_LABEL_TEXT;
			this._label.text = `${milestoneNameLabel} ${dateCache.getFormat(
				milestone.targetDate,
				settings.labelFormat
			)}`;
			return;
		}

		// Nothing set. Set default label
		this._label.text = `${MILESTONE_LINE_LABEL_TEXT}`;
	}
	/**
	 * Cleans up resources when the drawer is destroyed.
	 */
	public destroy(opts?: IDestroyOptions): void {
		super.destroy(opts);
		this._canvasAngularLand = null;
	}

	/**
	 * Hide the milestone icon in the timeline
	 */
	public hideIcon(): void {
		this._icon.visible = false;
	}
	/**
	 * Show the milestone icon in the timeline
	 */
	public showIcon(): void {
		this._icon.visible = true;
	}

	/**
	 * Sets up the layer structure for the milestone line.
	 * Creates a container and adds the line and label as children.
	 */
	private setup(): void {
		this._timelineContainer = this.parentApplication.layerManager.addLayer(
			LayerPaths.UI_TIMELINE_MILESTONE_LINE
		);

		this._components = new ComponentManager(this._container);
		this._components.add(`milestoneTapGesture`, new MilestoneTap(), this._canvasAngularLand, this._container);

		this._icon = new FancySprite(utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_TIMELINE_MILESTONE_ICON]);
		this.parentApplication.layerManager.addChildToLayer(LayerPaths.UI_TIMELINE_MILESTONE_LINE, this._icon);

		this._icon.visible = true;
		this._icon.active = true;
		this._icon.anchor.set(0, 0.5);
		this._icon.width = settings.iconWidth;
		this._icon.height = settings.iconHeight;

		this.destroyList.push(this._timelineContainer, this._components);
	}
	/**
	 * Sets up subscription to today line updates from the service.
	 * Triggers redraws when the today line data changes.
	 */
	private setupSubscribers(): void {
		this._canvasAngularLand.milestoneLineService.changed$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((milestone: IPlanMilestone) => this.draw(milestone));

		this.parentApplication.camera.changed$.pipe(takeUntil(this._shutdownSubject$)).subscribe((_) => {
			this.update();
		});
	}
}
